import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FileCheck, Shield, FileText, FolderClosed, ArrowRight, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";

const TYPE_META = {
  privacy: { label: "Privacy", icon: Shield },
  contract: { label: "Contratto", icon: FileText },
  annex: { label: "Allegati", icon: FolderClosed },
};

const fmtAge = (iso) => {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const days = Math.floor(ms / 86400000);
    if (days === 0) return "oggi";
    if (days === 1) return "ieri";
    if (days < 30) return `${days} giorni fa`;
    if (days < 60) return "1 mese fa";
    return `${Math.floor(days / 30)} mesi fa`;
  } catch { return ""; }
};

export const LegalDocumentsWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();

  // Only admin + super_admin see this widget (per Zorzutti security model:
  // IT/Viewer/Salesforce roles have read access to the underlying API but
  // can't modify documents, so highlighting it on their dashboard would
  // produce a frustrating "call to action" they cannot fulfil).
  const canWrite = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    if (!canWrite) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/legal-documents/_stats/summary`, getAuthHeader());
        if (mounted) setStats(r.data);
      } catch {
        // silently skip — widget hides on auth/server error
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [canWrite]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!canWrite || loading || !stats) return null;

  const hasStale = stats.stale_drafts.length > 0;
  const hasDrafts = stats.total_drafts > 0;
  const coverage = stats.total_slots > 0 ? Math.round((stats.total_active / stats.total_slots) * 100) : 0;

  // Zorzutti CR 02/07/2026 + Fabrizio CR 03/07/2026 — Gate 503 dynamic:
  // segnala TUTTE le lingue in uso (dealer attivi + customer non
  // cancellati + baseline IT) che non hanno privacy o contract attivi.
  // Prima solo IT era monitorata; ora onboardare un dealer BE o un
  // cliente polacco → banner rosso automatico se mancano FR/PL.
  const dealerLangs = stats.dealer_languages_in_use || ["it"];
  const privacyActiveLangs = new Set(stats.per_type?.privacy?.active_languages || []);
  const contractActiveLangs = new Set(stats.per_type?.contract?.active_languages || []);
  const annexActiveLangs = new Set(stats.per_type?.annex?.active_languages || []);
  const gateMissing = [];
  dealerLangs.forEach(lang => {
    if (!privacyActiveLangs.has(lang)) gateMissing.push(`Privacy ${lang.toUpperCase()}`);
    if (!contractActiveLangs.has(lang)) gateMissing.push(`Contratto ${lang.toUpperCase()}`);
  });

  // Fabrizio CR 06/07/2026 — Health Score dinamico
  // Post CR-Zorzutti "1 contratto per lingua" (matrice 3×N invece che
  // 3×18), il vero KPI di salute è: "quante delle lingue effettivamente
  // in uso (dealer attivi + customer non cancellati + baseline IT) hanno
  // TUTTI e 3 i tipi documento attivi?".
  //
  // ``dealerLangs`` è dinamico (2 → 15 lingue a seconda dei mercati) →
  // la % è più actionable della statica 3×18=54. Es. se operi solo IT+FR
  // e hai Privacy/Contract/Annex attivi in entrambe → 100% invece di
  // 6/54=11%.
  const typeCoverage = {
    privacy: {
      covered: dealerLangs.filter(l => privacyActiveLangs.has(l)),
      missing: dealerLangs.filter(l => !privacyActiveLangs.has(l)),
    },
    contract: {
      covered: dealerLangs.filter(l => contractActiveLangs.has(l)),
      missing: dealerLangs.filter(l => !contractActiveLangs.has(l)),
    },
    annex: {
      covered: dealerLangs.filter(l => annexActiveLangs.has(l)),
      missing: dealerLangs.filter(l => !annexActiveLangs.has(l)),
    },
  };
  const totalCells = dealerLangs.length * 3;
  const coveredCells = typeCoverage.privacy.covered.length + typeCoverage.contract.covered.length + typeCoverage.annex.covered.length;
  const healthScore = totalCells > 0 ? Math.round((coveredCells / totalCells) * 100) : 0;
  const scoreTone = healthScore >= 95
    ? { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-300", label: "OTTIMO" }
    : healthScore >= 70
      ? { bg: "bg-lojack-clause-500/10", border: "border-lojack-clause-500/40", text: "text-lojack-clause-300", label: "DA MIGLIORARE" }
      : { bg: "bg-red-500/10", border: "border-red-500/40", text: "text-red-300", label: "CRITICO" };
  const topMissing = [];
  ["privacy", "contract", "annex"].forEach(t => {
    typeCoverage[t].missing.slice(0, 3).forEach(l => topMissing.push({ type: t, lang: l }));
  });

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="legal-documents-widget">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/15 text-blue-400 flex items-center justify-center"><FileCheck size={20} /></div>
          <div>
            <h3 className="text-white font-bold">Documenti Legali</h3>
            <p className="text-gray-500 text-xs">Privacy · Contratto · Allegati · 18 lingue</p>
          </div>
        </div>
        <button onClick={() => navigate("/admin/legal")} className="px-4 py-2 bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/40 rounded-xl flex items-center gap-2 text-sm font-medium transition-colors" data-testid="widget-legal-cta">
          Gestisci <ArrowRight size={14} />
        </button>
      </div>

      {gateMissing.length > 0 && (
        <div className="bg-red-600/15 border-2 border-red-500/70 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="widget-gate-503-banner">
          <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs">
            <p className="text-red-300 font-bold uppercase tracking-wide">
              Gate 503 attivo — {gateMissing.length} document{gateMissing.length === 1 ? "o" : "i"} mancant{gateMissing.length === 1 ? "e" : "i"}
            </p>
            <p className="text-red-200/90 mt-0.5">
              Manca il documento attivo per: <strong>{gateMissing.join(", ")}</strong>.
              {" "}Lingue monitorate (dealer + clienti + baseline IT): <em>{dealerLangs.map(l => l.toUpperCase()).join(", ")}</em>.
              {" "}I clienti nelle lingue mancanti riceveranno HTTP 503 sul preview privacy finché non attivi il PDF.
            </p>
          </div>
          <button onClick={() => navigate("/admin/legal")} className="px-3 py-1 bg-red-500 hover:bg-red-400 text-white text-xs font-bold rounded-lg shrink-0" data-testid="widget-gate-503-cta">Attiva ora</button>
        </div>
      )}

      {/* Fabrizio CR 06/07/2026 — Health Score compatto sulle lingue in uso */}
      <div className={`${scoreTone.bg} ${scoreTone.border} border-2 rounded-xl p-4 mb-4`} data-testid="widget-health-score-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <p className={`text-[10px] font-bold uppercase tracking-widest ${scoreTone.text}`} data-testid="widget-health-score-label">
              Health Score · {scoreTone.label}
            </p>
            <p className="text-gray-500 text-[11px] mt-0.5">
              Copertura documenti attivi sulle <strong className="text-gray-300">{dealerLangs.length} lingue in uso</strong> (dealer + clienti + baseline IT).
              Aggiornata in real-time da <em>/admin/legal-documents/_stats/summary</em>.
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className={`text-4xl font-black tabular-nums ${scoreTone.text}`} data-testid="widget-health-score-value">{healthScore}%</p>
            <p className="text-gray-500 text-[10px] tabular-nums">{coveredCells}/{totalCells} celle attive</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(typeCoverage).map(([type, cov]) => {
            const meta = TYPE_META[type] || { label: type };
            const pct = dealerLangs.length > 0 ? Math.round((cov.covered.length / dealerLangs.length) * 100) : 0;
            const perTypeTone = pct === 100 ? "text-emerald-300" : pct >= 50 ? "text-lojack-clause-300" : "text-red-300";
            return (
              <div key={type} className="bg-black/30 rounded-lg px-2.5 py-2" data-testid={`widget-health-type-${type}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{meta.label}</span>
                  <span className={`text-xs font-bold tabular-nums ${perTypeTone}`}>{cov.covered.length}/{dealerLangs.length}</span>
                </div>
                <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div className={`h-full ${pct === 100 ? "bg-emerald-500" : pct >= 50 ? "bg-lojack-clause-500" : "bg-red-500"} transition-all`} style={{ width: `${pct}%` }} data-testid={`widget-health-type-${type}-bar`}></div>
                </div>
                {cov.missing.length > 0 && (
                  <p className="text-[10px] text-gray-500 mt-1 truncate" title={`Mancanti: ${cov.missing.map(l => l.toUpperCase()).join(", ")}`}>
                    Mancanti: <strong className="text-gray-300">{cov.missing.map(l => l.toUpperCase()).join(", ")}</strong>
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {topMissing.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-800/60" data-testid="widget-health-quickfix">
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1.5">Attiva con 1 click</p>
            <div className="flex flex-wrap gap-1.5">
              {topMissing.slice(0, 6).map(({ type, lang }) => (
                <button
                  key={`${type}-${lang}`}
                  onClick={() => navigate(`/admin/legal#section-${type}?highlight=${lang}`)}
                  className="px-2 py-0.5 bg-white/5 hover:bg-white/10 border border-gray-700 hover:border-lojack-blue-500/60 text-gray-300 text-[10px] rounded-md flex items-center gap-1 transition-colors"
                  data-testid={`widget-health-quickfix-${type}-${lang}`}
                >
                  <span className="uppercase font-semibold text-gray-400">{lang}</span>
                  <span className="text-gray-500">·</span>
                  <span>{TYPE_META[type]?.label || type}</span>
                  <ArrowRight size={10} className="text-lojack-blue-400" />
                </button>
              ))}
              {topMissing.length > 6 && (
                <span className="px-2 py-0.5 text-gray-500 text-[10px]">+{topMissing.length - 6}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {hasStale && (
        <div className="bg-lojack-clause-500/10 border-2 border-lojack-clause-500/50 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="widget-stale-banner">
          <AlertTriangle size={16} className="text-lojack-clause-400 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs">
            <p className="text-lojack-clause-300 font-semibold">{stats.stale_drafts.length} bozza/e in attesa di attivazione da oltre 7 giorni</p>
            <p className="text-lojack-clause-200/80 mt-0.5">
              {stats.stale_drafts.slice(0, 2).map(d => `${TYPE_META[d.type]?.label || d.type}/${d.language.toUpperCase()} (${d.version})`).join(", ")}
              {stats.stale_drafts.length > 2 && ` · +${stats.stale_drafts.length - 2}`}
            </p>
          </div>
          <button onClick={() => navigate("/admin/legal")} className="px-3 py-1 bg-lojack-clause-500 hover:bg-lojack-clause-400 text-black text-xs font-bold rounded-lg shrink-0">Rivedi</button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-[#0d0d0d] border border-lojack-blue-500/20 rounded-xl p-3 text-center" data-testid="widget-kpi-active">
          <CheckCircle2 size={14} className="mx-auto text-lojack-blue-400 mb-1" />
          <p className="text-2xl font-bold text-lojack-blue-300 tabular-nums">{stats.total_active}</p>
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">Attivi</p>
        </div>
        <div className={`bg-[#0d0d0d] border rounded-xl p-3 text-center ${hasDrafts ? 'border-lojack-clause-500/30' : 'border-gray-800'}`} data-testid="widget-kpi-drafts">
          <Clock size={14} className={`mx-auto ${hasDrafts ? 'text-lojack-clause-400' : 'text-gray-500'} mb-1`} />
          <p className={`text-2xl font-bold tabular-nums ${hasDrafts ? 'text-lojack-clause-300' : 'text-gray-500'}`}>{stats.total_drafts}</p>
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">Bozze</p>
        </div>
        <div className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-3 text-center" data-testid="widget-kpi-missing">
          <p className="text-2xl font-bold text-gray-400 tabular-nums">{stats.total_missing}</p>
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">Slot vuoti</p>
          <p className="text-gray-600 text-[9px] mt-0.5">su {stats.total_slots}</p>
        </div>
      </div>

      <div className="space-y-1.5 mb-3" data-testid="widget-per-type">
        {Object.entries(stats.per_type).map(([type, info]) => {
          const meta = TYPE_META[type] || { label: type, icon: FileText };
          const Icon = meta.icon;
          const pct = info.active + info.draft + info.missing_languages > 0
            ? Math.round((info.active / 18) * 100)
            : 0;
          return (
            <div key={type} className="flex items-center gap-3 text-xs" data-testid={`widget-type-${type}`}>
              <div className="flex items-center gap-2 w-28 shrink-0">
                <Icon size={12} className="text-gray-400" />
                <span className="text-gray-300">{meta.label}</span>
              </div>
              <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-lojack-blue-500/70 transition-all" style={{ width: `${pct}%` }}></div>
              </div>
              <span className="text-gray-500 w-20 text-right tabular-nums">{info.active}/18 attive</span>
              {info.draft > 0 && <span className="px-1.5 py-0.5 bg-lojack-clause-500/15 text-lojack-clause-400 rounded text-[10px] font-semibold">{info.draft} bozza</span>}
            </div>
          );
        })}
      </div>

      {stats.last_modified && (
        <div className="border-t border-gray-800 pt-3 text-xs text-gray-500" data-testid="widget-last-modified">
          <span className="text-gray-400">Ultima modifica:</span>{" "}
          <strong className="text-gray-300">{TYPE_META[stats.last_modified.type]?.label}/{stats.last_modified.language.toUpperCase()}</strong>{" "}
          v.{stats.last_modified.version}{" "}
          <span className="text-gray-600">· {stats.last_modified.uploaded_by} · {fmtAge(stats.last_modified.uploaded_at)}</span>
          {!stats.last_modified.is_active && <span className="ml-2 px-1.5 py-0.5 bg-lojack-clause-500/15 text-lojack-clause-400 rounded text-[10px]">bozza</span>}
        </div>
      )}

      <div className="mt-3 text-[10px] text-gray-600">
        Copertura globale: <strong className="text-gray-400">{coverage}%</strong> ({stats.total_active}/{stats.total_slots} slot lingua·tipo)
      </div>
    </div>
  );
};
